import posthog from 'posthog-js';
import { posthogEnvironment, posthogRelease } from '../../posthog/sharedConfig';
export type ObservabilityContext = Record<string, unknown>;

const contextKeyAliases: Record<string, string> = {
  traceId: 'trace_id',
  spanId: 'span_id',
  companyId: 'company_id',
  attikUserId: 'attik_user_id',
  userId: 'attik_user_id',
  schedulerContactId: 'scheduler_contact_id',
  quoteId: 'quote_id',
  draftQuoteId: 'draft_quote_id',
  inspectionId: 'inspection_id',
  jobId: 'job_id',
  contactId: 'contact_id',
  serviceId: 'service_id',
  slotCount: 'slot_count',
  retryCount: 'retry_count',
  statusCode: 'http_status_code',
  bookingMethod: 'booking_method',
  wcLeadId: 'wc_lead_id',
  reportId: 'report_id',
  authMethod: 'auth_method',
  callbackPath: 'callback_path',
  emailDomain: 'email_domain',
  createQuoteHold: 'create_quote_hold',
  isUpdate: 'is_update',
  schedulerRunId: 'scheduler_run_id',
};

/** Discriminator for public /scheduler vs staff scheduling tools. */
function inferSchedulerModeFromStep(
  step: string
): 'online' | 'csr' | undefined {
  if (step.startsWith('online_')) return 'online';
  if (step.startsWith('csr_')) return 'csr';
  return undefined;
}

const schedulerWorkflowAliases: Record<
  string,
  { workflow: string; action: string; scheduler_mode: 'online' | 'csr' }
> = {
  online_quote_commit: {
    workflow: 'scheduler.quote.commit',
    action: 'quote_commit',
    scheduler_mode: 'online',
  },
  csr_quote_commit: {
    workflow: 'scheduler.quote.commit',
    action: 'quote_commit',
    scheduler_mode: 'csr',
  },
  online_inspection_book: {
    workflow: 'scheduler.inspection.book',
    action: 'book_job',
    scheduler_mode: 'online',
  },
  csr_inspection_book: {
    workflow: 'scheduler.inspection.book',
    action: 'book_job',
    scheduler_mode: 'csr',
  },
  online_resume_draft: {
    workflow: 'scheduler.draft.resume',
    action: 'resume_draft',
    scheduler_mode: 'online',
  },
  online_autosave_draft: {
    workflow: 'scheduler.draft.autosave',
    action: 'autosave_draft',
    scheduler_mode: 'online',
  },
  online_optimal_slots: {
    workflow: 'scheduler.slots.optimal',
    action: 'fetch_optimal_slots',
    scheduler_mode: 'online',
  },
  csr_quote_draft: {
    workflow: 'scheduler.draft.save',
    action: 'save_draft',
    scheduler_mode: 'csr',
  },
};

function toSnakeCase(value: string): string {
  return value
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[\s.-]+/g, '_')
    .toLowerCase();
}

export function normalizeObservabilityContext(
  context?: ObservabilityContext
): ObservabilityContext {
  if (!context) return {};

  const normalized: ObservabilityContext = {};
  for (const [key, value] of Object.entries(context)) {
    normalized[contextKeyAliases[key] ?? toSnakeCase(key)] = value;
  }
  return normalized;
}

function posthogProperty(key: string): unknown {
  try {
    return posthog.get_property(key);
  } catch {
    return undefined;
  }
}

function posthogDistinctId(): string | undefined {
  try {
    return posthog.get_distinct_id();
  } catch {
    return undefined;
  }
}

function posthogSessionId(): string | undefined {
  try {
    return posthog.get_session_id();
  } catch {
    return undefined;
  }
}

/** Placeholder when not signed in / scope not set, so logs always carry identity keys. */
const LOG_IDENTITY_UNKNOWN = 'unknown';

/**
 * Tags + user from the active client telemetry scope. Logger calls do not attach
 * scope tags to log attributes automatically.
 *
 * Always includes `company_id`, `user_id`, and `attik_user_id` (same value as user_id when known).
 */
export function scopeFilterContextForLogs(): ObservabilityContext {
  const companyRaw = posthogProperty('company_id');
  const company_id =
    companyRaw != null && String(companyRaw).trim() !== ''
      ? String(companyRaw)
      : LOG_IDENTITY_UNKNOWN;

  const attikTag = posthogProperty('attik_user_id') ?? posthogDistinctId();
  const userFromTag =
    attikTag != null && String(attikTag).trim() !== ''
      ? String(attikTag)
      : undefined;

  const user_id = userFromTag ?? LOG_IDENTITY_UNKNOWN;

  const out: ObservabilityContext = {
    company_id,
    user_id,
    attik_user_id: user_id,
  };

  const sessionId = posthogSessionId();
  if (sessionId) out.$session_id = sessionId;

  const rawRun = posthogProperty('scheduler_run_id');
  if (rawRun != null && String(rawRun).trim() !== '') {
    out.scheduler_run_id = String(rawRun);
  }
  return out;
}

export function withTraceContext(
  context?: ObservabilityContext
): ObservabilityContext {
  return {
    ...scopeFilterContextForLogs(),
    ...normalizeObservabilityContext(context),
  };
}

/** Pipe-delimited message tail so log messages show correlation without opening attributes. */
export function schedulerStepMessageLine(
  title: string,
  ctx: ObservabilityContext
): string {
  const merged: ObservabilityContext = {
    ...scopeFilterContextForLogs(),
    ...ctx,
  };
  const parts: string[] = [title];
  if (merged.step != null && merged.step !== '') {
    parts.push(`step=${String(merged.step)}`);
  }
  if (merged.workflow != null && merged.workflow !== '') {
    parts.push(`workflow=${String(merged.workflow)}`);
  }
  parts.push(`company_id=${String(merged.company_id ?? LOG_IDENTITY_UNKNOWN)}`);
  parts.push(`user_id=${String(merged.user_id ?? LOG_IDENTITY_UNKNOWN)}`);
  return parts.join(' | ');
}

/** Auth and other logs: MESSAGE column includes tenant + user for quick scanning. */
export function formatLogMessageWithIdentity(
  title: string,
  context?: ObservabilityContext
): string {
  const merged: ObservabilityContext = {
    ...scopeFilterContextForLogs(),
    ...normalizeObservabilityContext(context ?? {}),
  };
  return `${title} | company_id=${String(merged.company_id ?? LOG_IDENTITY_UNKNOWN)} | user_id=${String(merged.user_id ?? LOG_IDENTITY_UNKNOWN)}`;
}

export function withSchedulerWorkflowContext(
  step: string,
  context?: ObservabilityContext
): ObservabilityContext {
  const normalized = normalizeObservabilityContext(context);
  const workflow = schedulerWorkflowAliases[step];
  const mode =
    workflow?.scheduler_mode ?? inferSchedulerModeFromStep(step);

  // Drop fields callers might duplicate; canonical values come from the step / alias map.
  const rest = { ...normalized };
  delete rest.scheduler_mode;
  delete rest.workflow;
  delete rest.action;
  delete rest.flow;

  return {
    feature: 'scheduling',
    step,
    ...rest,
    ...(workflow ?? {}),
    ...(mode ? { scheduler_mode: mode } : {}),
  };
}

export function schedulerSpanOptions(step: string): {
  name: string;
  op: 'ui.action';
  attributes: Record<string, string>;
} {
  const workflow = schedulerWorkflowAliases[step];
  const mode =
    workflow?.scheduler_mode ?? inferSchedulerModeFromStep(step);
  return {
    name: workflow?.workflow ?? step,
    op: 'ui.action',
    attributes: {
      feature: 'scheduling',
      action: workflow?.action ?? step,
      workflow: workflow?.workflow ?? step,
      surface: 'web',
      ...(mode ? { scheduler_mode: mode } : {}),
    },
  };
}

export function eventTagsFromContext(
  context?: ObservabilityContext
): Record<string, string> {
  const normalized = normalizeObservabilityContext(context);
  const allowedTagKeys = [
    'company_id',
    'attik_user_id',
    'user_id',
    'scheduler_contact_id',
    'feature',
    'workflow',
    'action',
    'result',
    'surface',
    'scheduler_mode',
    'scheduler_run_id',
  ];

  const tags: Record<string, string> = {};
  for (const key of allowedTagKeys) {
    const value = normalized[key];
    const s = value != null && value !== '' ? String(value) : '';
    if (s !== '' && s !== LOG_IDENTITY_UNKNOWN) {
      tags[key] = s;
    }
  }
  return tags;
}

export function captureBoundaryException(
  error: unknown,
  context?: ObservabilityContext
): void {
  const normalized = withTraceContext(context);
  const release = posthogRelease();
  posthog.captureException(
    error instanceof Error ? error : new Error(String(error)),
    {
      service: 'attik-frontend',
      runtime: 'browser',
      environment: posthogEnvironment(),
      ...(release ? { release } : {}),
      ...normalized,
    }
  );
}
